import { useState } from "react";
import { toast } from "sonner";
import api, { fmtMoney, fmtDate } from "@/lib/api";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import CuentaCombobox from "@/components/CuentaCombobox";

/* Crea en el sistema el movimiento que está en el extracto y no existe:
   gasto de estructura, movimiento con cuenta contable o traspaso entre cajas.
   (Cobro/pago usan ConvertirMovimientoDialog.) */
export default function CrearDesdeExtractoDialog({ mov, modo, cajas, cuentas, onClose, onDone }) {
  const [cuenta, setCuenta] = useState("");
  const [otraCaja, setOtraCaja] = useState("");
  const [concepto, setConcepto] = useState("");
  const [obs, setObs] = useState("");
  const [saving, setSaving] = useState(false);
  if (!mov) return null;
  const esEgreso = mov.tipo === "egreso";
  const titulo = { gasto: "Crear gasto de estructura", movimiento: "Crear movimiento con cuenta contable", traspaso: "Crear traspaso entre cajas" }[modo];
  const cuentasFiltradas = modo === "gasto" ? cuentas.filter((c) => c.tipo === "egreso" && c.imputable !== false) : cuentas;

  const confirmar = async () => {
    setSaving(true);
    try {
      if (modo === "gasto") {
        if (!cuenta) return toast.error("Elegí la cuenta del gasto");
        await api.post("/conciliacion/crear-gasto", { mov_id: mov.id, cuenta_codigo: cuenta, concepto: concepto || null, beneficiario: obs || null });
        toast.success("Gasto creado y conciliado con el extracto");
      } else if (modo === "movimiento") {
        if (!cuenta) return toast.error("Elegí la cuenta contable");
        const { data } = await api.post("/movimientos-caja/clasificar-batch", { items: [{ mov_id: mov.id, cuenta_codigo: cuenta, observacion: obs || null }] });
        if (data.errors?.length) return toast.error(data.errors[0].reason);
        toast.success("Movimiento creado en el sistema y conciliado");
      } else {
        if (!otraCaja) return toast.error("Elegí la otra caja del traspaso");
        await api.post("/conciliacion/crear-traspaso", { mov_id: mov.id, otra_caja_id: otraCaja, concepto: concepto || null });
        toast.success("Traspaso creado y conciliado con el extracto");
      }
      onDone?.();
      onClose?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo crear");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose?.()}>
      <DialogContent className="max-w-md" data-testid="crear-desde-extracto-dialog">
        <DialogHeader><DialogTitle className="font-heading">{titulo}</DialogTitle></DialogHeader>
        <div className="rounded-sm border border-[#E5E5E5] bg-neutral-50 px-3 py-2 text-[12px]">
          <div className="font-mono text-[11px] text-neutral-500">{fmtDate(mov.fecha)} · {mov.caja_nombre}</div>
          <div className="truncate" title={mov.concepto}>{mov.concepto}</div>
          <div className={`font-mono font-semibold ${esEgreso ? "num-neg" : "num-pos"}`}>{esEgreso ? "-" : "+"} {fmtMoney(mov.monto, mov.moneda)}</div>
        </div>
        <div className="space-y-3 mt-2">
          {modo !== "traspaso" && (
            <div>
              <Label className="text-[12px]">{modo === "gasto" ? "Cuenta de gasto" : "Cuenta contable"}</Label>
              <CuentaCombobox value={cuenta} onChange={setCuenta} cuentas={cuentasFiltradas} testid="crear-extracto-cuenta" compact />
            </div>
          )}
          {modo === "traspaso" && (
            <div>
              <Label className="text-[12px]">{esEgreso ? "La plata fue a la caja…" : "La plata vino de la caja…"}</Label>
              <Select value={otraCaja} onValueChange={setOtraCaja}>
                <SelectTrigger className="rounded-sm mt-1 h-9" data-testid="crear-extracto-otra-caja"><SelectValue placeholder="Elegir caja" /></SelectTrigger>
                <SelectContent>
                  {cajas.filter((c) => c.id !== mov.caja_id && (c.moneda || "ARS") === (mov.moneda || "ARS")).map((c) => (
                    <SelectItem key={c.id} value={c.id}>{c.nombre}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
          {modo !== "movimiento" && (
            <div>
              <Label className="text-[12px]">Concepto <span className="text-neutral-400">(opcional, por defecto el del extracto)</span></Label>
              <Input className="rounded-sm mt-1 h-9" value={concepto} onChange={(e) => setConcepto(e.target.value)} data-testid="crear-extracto-concepto" />
            </div>
          )}
          {modo !== "traspaso" && (
            <div>
              <Label className="text-[12px]">{modo === "gasto" ? "Beneficiario" : "Observación"} <span className="text-neutral-400">(opcional)</span></Label>
              <Input className="rounded-sm mt-1 h-9" value={obs} onChange={(e) => setObs(e.target.value)} data-testid="crear-extracto-obs" />
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" className="rounded-sm" onClick={onClose} data-testid="crear-extracto-cancelar">Cancelar</Button>
          <Button className="rounded-sm bg-[#6B1F24] hover:bg-[#571519]" disabled={saving} onClick={confirmar} data-testid="crear-extracto-confirmar">
            {saving ? "Creando…" : "Crear y conciliar"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
